if (!customElements.get('mte-reviews')) {
  customElements.define('mte-reviews', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.grid = this.querySelector('[data-review-grid]');
      this.dialog = this.querySelector('[data-review-dialog]');
      this.dialogContent = this.querySelector('[data-review-dialog-content]');
      this.more = this.querySelector('[data-review-next]');
      this.empty = this.querySelector('[data-review-empty]');
      this.nextUrl = this.querySelector('[data-review-pagination]')?.dataset.nextUrl || '';
      this.pageSize = Number(this.dataset.pageSize) || 8;
      this.visibleLimit = this.pageSize;
      this.sort = this.dataset.initialSort || 'featured';
      this.rating = this.dataset.initialRating || 'all';
      this.loading = null;
      this.lastWidth = 0;
      this.openingCard = null;
      if (!this.grid) return;
      this.addEventListener('click', (event) => this.click(event), { signal: this.abort.signal });
      this.addEventListener('keydown', (event) => {
        const card = event.target.closest('.mter-review');
        if (card && !event.target.closest('dialog') && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          this.openReview(card);
        }
      }, { signal: this.abort.signal });
      this.dialog?.addEventListener('close', () => this.cleanModal(), { signal: this.abort.signal });
      this.dialog?.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, { signal: this.abort.signal });
      this.addEventListener('load', () => this.layout(), { signal: this.abort.signal, capture: true });
      document.addEventListener('shopify:block:select', (event) => {
        const card = this.querySelector(`[data-block-id="${CSS.escape(event.detail.blockId)}"]`);
        if (!card) return;
        this.rating = 'all';
        this.sort = 'featured';
        this.visibleLimit = Math.max(this.pageSize, this.cards().indexOf(card) + 1);
        this.apply();
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, { signal: this.abort.signal });
      this.resize = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width || 0;
        if (width !== this.lastWidth) { this.lastWidth = width; this.layout(); }
      });
      this.resize.observe(this.grid);
      document.fonts?.ready.then(() => { if (this.isConnected) this.layout(); });
      this.refreshDates();
      this.apply();
      if (this.dataset.mode === 'metaobjects' && (this.sort !== 'featured' || this.rating !== 'all')) this.loadAll().then(() => this.apply()).catch((error) => this.reportFailure(error));
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.cleanModal();
    }

    cards() { return [...this.grid.querySelectorAll(':scope > .mter-review')]; }

    refreshDates(scope = this) {
      scope.querySelectorAll('[data-review-locale-date]').forEach((element) => {
        const raw = element.dataset.reviewLocaleDate;
        const value = new Date(/^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T12:00:00` : raw);
        if (!Number.isNaN(value.getTime())) element.textContent = value.toLocaleDateString();
      });
    }

    async click(event) {
      const filter = event.target.closest('[data-review-filter]');
      const sort = event.target.closest('[data-review-sort]');
      const more = event.target.closest('[data-review-next]');
      const close = event.target.closest('[data-review-close]');
      if (close) { this.dialog.close(); return; }
      if (filter || sort) {
        event.preventDefault();
        if (filter) {
          this.rating = this.rating === filter.dataset.reviewFilter ? 'all' : filter.dataset.reviewFilter;
          filter.closest('details').open = false;
        }
        if (sort) { this.sort = sort.dataset.reviewSort; sort.closest('details').open = false; }
        this.visibleLimit = this.pageSize;
        try { await this.loadAll(); this.apply(); } catch (error) { this.reportFailure(error); }
        return;
      }
      if (more) {
        event.preventDefault();
        if (this.loading) return;
        try {
          if (this.nextUrl) await this.loadAll();
          this.visibleLimit += this.pageSize;
          this.apply();
        } catch (error) { this.reportFailure(error); }
        return;
      }
      const card = event.target.closest('.mter-review');
      if (card && !event.target.closest('dialog')) this.openReview(card);
    }

    matches(card) { return this.rating === 'all' || card.dataset.reviewRating === this.rating; }

    apply() {
      if (!this.isConnected) return;
      const cards = this.cards();
      cards.sort((a, b) => {
        const featured = Number(a.dataset.reviewOrder) - Number(b.dataset.reviewOrder);
        if (this.sort === 'newest') return (Date.parse(b.dataset.reviewDate) || 0) - (Date.parse(a.dataset.reviewDate) || 0) || featured;
        if (this.sort === 'highest_ratings') return Number(b.dataset.reviewRating) - Number(a.dataset.reviewRating) || featured;
        if (this.sort === 'lowest_ratings') return Number(a.dataset.reviewRating) - Number(b.dataset.reviewRating) || featured;
        return featured;
      });
      let shown = 0;
      let matching = 0;
      cards.forEach((card) => {
        const match = this.matches(card);
        if (match) matching += 1;
        card.hidden = !match || shown >= this.visibleLimit;
        if (!card.hidden) shown += 1;
        this.grid.append(card);
      });
      this.empty.hidden = matching > 0;
      this.more.hidden = matching <= this.visibleLimit && !this.nextUrl;
      this.querySelectorAll('[data-review-filter]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.reviewFilter === this.rating)));
      this.querySelectorAll('[data-review-sort]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.reviewSort === this.sort)));
      this.layout();
    }

    layout() {
      if (!this.grid || !this.isConnected) return;
      const desktop = matchMedia('(min-width: 621px)').matches;
      const gap = parseFloat(getComputedStyle(this).getPropertyValue('--mter-gap')) || 14;
      const cards = this.cards();
      cards.forEach((card) => { card.style.gridRowEnd = ''; });
      if (!desktop) return;
      const spans = cards.filter((card) => !card.hidden).map((card) => ({ card, span: Math.ceil((card.getBoundingClientRect().height + gap) / 4) }));
      spans.forEach(({ card, span }) => { card.style.gridRowEnd = `span ${span}`; });
    }

    async loadAll() {
      if (this.dataset.mode !== 'metaobjects') return;
      while (this.nextUrl && this.isConnected) await this.loadPage();
    }

    async loadPage() {
      if (this.loading) return this.loading;
      if (!this.nextUrl) return;
      const next = new URL(this.nextUrl, location.href);
      if (next.origin !== location.origin) throw new Error('Review pagination must stay on the Shopify destination origin.');
      next.searchParams.set('section_id', this.dataset.sectionId);
      this.setAttribute('aria-busy', 'true');
      this.loading = (async () => {
        const response = await fetch(next.toString(), { credentials: 'same-origin', signal: this.abort.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Review pagination returned ${response.status}.`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = html.querySelector(`mte-reviews[data-section-id="${CSS.escape(this.dataset.sectionId)}"]`);
        if (!section) throw new Error('Review section is missing in pagination response.');
        const ids = new Set(this.cards().map((card) => card.dataset.reviewId));
        let added = 0;
        section.querySelectorAll('[data-review-grid] > .mter-review').forEach((card) => {
          if (ids.has(card.dataset.reviewId)) return;
          ids.add(card.dataset.reviewId);
          const imported = document.importNode(card, true);
          this.refreshDates(imported);
          this.grid.append(imported);
          added += 1;
        });
        const nextUrl = section.querySelector('[data-review-pagination]')?.dataset.nextUrl || '';
        if (!added && nextUrl) throw new Error('Review pagination repeated a page.');
        this.nextUrl = nextUrl;
      })();
      try { await this.loading; } finally { this.loading = null; this.removeAttribute('aria-busy'); }
    }

    openReview(card) {
      const content = card.querySelector('[data-review-modal]');
      if (!content || !this.dialog) return;
      this.openingCard = card;
      this.dialogContent.replaceChildren(content.content.cloneNode(true));
      this.refreshDates(this.dialogContent);
      this.dialog.showModal();
      this.dialog.querySelector('[data-review-close]')?.focus();
    }

    cleanModal() {
      this.dialogContent?.querySelectorAll('video').forEach((video) => video.pause());
      this.dialogContent?.replaceChildren();
      if (this.openingCard?.isConnected) this.openingCard.focus({ preventScroll: true });
      this.openingCard = null;
    }

    reportFailure(error) {
      if (error.name !== 'AbortError') console.error('MTE reviews:', error);
    }
  });
}
